import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import BlogCard from "../components/BlogCard";
import Pagination from "../components/Pagination";
import TagFilter from "../components/TagFilter";
import { categories } from "../data/blogData";
import { getAllPosts } from "../services/blogService";

const PER_PAGE = 9;

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const urlCategory = searchParams.get("category") || "All Posts";
  const urlSearch = searchParams.get("search") || "";
  const urlTags = searchParams.get("tags")
    ? searchParams.get("tags").split(",")
    : [];

  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(urlCategory);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [activeTags, setActiveTags] = useState(urlTags);

  useEffect(() => {
    let cancelled = false;
    getAllPosts({ publishedOnly: true })
      .then((posts) => { if (!cancelled) setAllPosts(posts); })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    let result = allPosts;

    if (activeCategory !== "All Posts") {
      result = result.filter((p) => p.category === activeCategory);
    }

    if (activeTags.length > 0) {
      result = result.filter((p) =>
        activeTags.some((tag) => p.tags && p.tags.includes(tag))
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          (p.excerpt && p.excerpt.toLowerCase().includes(q)) ||
          (p.content && p.content.toLowerCase().includes(q)) ||
          (p.tags && p.tags.some((t) => t.toLowerCase().includes(q)))
      );
    }

    return result;
  }, [allPosts, activeCategory, searchQuery, activeTags]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const pagePosts = useMemo(() => {
    const start = (safePage - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, safePage]);

  const featured =
    safePage === 1 &&
    activeCategory === "All Posts" &&
    activeTags.length === 0 &&
    !searchQuery.trim()
      ? pagePosts[0]
      : null;
  const gridPosts = featured ? pagePosts.slice(1) : pagePosts;

  const updateUrl = (page, category, search, tags) => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page);
    if (category && category !== "All Posts") params.set("category", category);
    if (search) params.set("search", search);
    if (tags && tags.length > 0) params.set("tags", tags.join(","));
    setSearchParams(params, { replace: true });
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    updateUrl(1, cat, searchQuery, activeTags);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    updateUrl(1, activeCategory, value, activeTags);
  };

  const handleTagToggle = (tag) => {
    const newTags = activeTags.includes(tag)
      ? activeTags.filter((t) => t !== tag)
      : [...activeTags, tag];
    setActiveTags(newTags);
    updateUrl(1, activeCategory, searchQuery, newTags);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setActiveCategory("All Posts");
    setActiveTags([]);
    setSearchParams({});
  };

  if (loading) {
    return (
      <section className="min-h-screen pt-28 pb-20">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="liquid-blob absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
            <div className="liquid-blob absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[120px]" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-20 pb-16">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight max-w-4xl">
              Insights &{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-tech-green">
                Engineering Stories
              </span>
            </h1>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-20 text-center py-20">
          <div className="w-10 h-10 border-2 border-cyber-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading articles...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen pt-28 pb-20">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="liquid-blob absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
          <div className="liquid-blob absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[120px]" />
          <div className="liquid-blob absolute -bottom-40 left-1/2 w-[450px] h-[450px] rounded-full bg-purple-600/10 blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-20 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight max-w-4xl">
              Insights &{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-tech-green">
                Engineering Stories
              </span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl mt-6 max-w-2xl">
              Cybersecurity, networking, infrastructure, software
              engineering, and modern digital systems.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Search + Categories */}
      <div className="max-w-7xl mx-auto px-6 lg:px-20 mb-12">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-8">
          <div className="relative w-full md:w-80">
            <svg
              viewBox="0 0 24 24"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 fill-gray-500"
            >
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-cyber-blue focus:shadow-[0_0_12px_rgba(0,163,255,0.15)] transition-all"
            />
          </div>
          <p className="text-gray-500 text-sm">
            {filtered.length} article{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? "bg-cyber-blue text-black"
                  : "glass text-gray-300 hover:text-white hover:bg-white/10"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <TagFilter activeTags={activeTags} onTagToggle={handleTagToggle} />
      </div>

      {/* Featured Article — page 1 only */}
      {featured && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-7xl mx-auto px-6 lg:px-20 mb-14"
        >
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-6">
            Featured Article
          </h2>
          <BlogCard post={featured} featured />
        </motion.div>
      )}

      {/* Blog Grid */}
      {gridPosts.length > 0 && (
        <motion.div
          initial="initial"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={{
            initial: {},
            show: { transition: { staggerChildren: 0.05 } },
          }}
          className="max-w-7xl mx-auto px-6 lg:px-20 grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {gridPosts.map((post) => (
            <motion.div
              key={post.slug || post.id}
              variants={{
                initial: { opacity: 0, y: 30 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
              }}
            >
              <BlogCard post={post} />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Empty state */}
      {gridPosts.length === 0 && (
        <div className="max-w-7xl mx-auto px-6 lg:px-20 text-center py-20">
          <p className="text-gray-500 text-lg">
            {searchQuery
              ? `No results for "${searchQuery}"${
                  activeCategory !== "All Posts"
                    ? ` in "${activeCategory}"`
                    : ""
                }.`
              : `No articles in this category yet.`}
          </p>
          {(searchQuery || activeCategory !== "All Posts" || activeTags.length > 0) && (
            <button
              onClick={clearFilters}
              className="mt-4 text-cyber-blue hover:underline text-sm"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          basePath="/blog"
          category={activeCategory}
          search={searchQuery}
        />
      )}
    </section>
  );
}
